import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FiHome, FiUsers, FiChevronDown, FiHelpCircle, FiBell, FiSearch, FiCheck, FiSliders, FiUser, FiLogOut, FiGlobe } from 'react-icons/fi';
import { IoMdMegaphone } from 'react-icons/io';
import ShieldCheckIcon from './ShieldCheckIcon';
import api from '../../api/api';
import { IGlobalState } from '../../model/IGlobalState';
import { User } from '../../model/User';
import { startSetStock } from '../../actions/CurrentStock';
import NotificationsMenu from './notifications/NotificationsMenu';
import HelpPanel from './help/HelpPanel';
import { useNotifications } from './notifications/notificationsStore';

interface Client { id: string; name: string; ticker: string; registry: string | null }

// last selected client per browser (cleared on sign-in); state.currentStock is the source of truth once loaded
export const CURRENT_TICKER_KEY = 'investi_current_stock_ticker';

export const initials = (s: string) => s.split(/[\s.@_-]+/).filter(Boolean).slice(0, 2).map(x => x[0].toUpperCase()).join('');

// profile name when set, else the sign-in email
export const displayName = (user: User) => [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username;

// profile photo, or initials when there is none
export const Avatar: React.FC<{ user: User; className?: string }> = ({ user, className = 'avatar' }) =>
    user.photoVersion
        ? <img className={`${className} photo`} src={api().getProfilePhotoUrl(user.photoVersion)} alt="" />
        : <span className={className}>{initials(displayName(user))}</span>;

const Header: React.FC = () => {
    const user = useSelector((state: IGlobalState) => state.user);
    const currentStock = useSelector((state: IGlobalState) => state.currentStock);
    const dispatch = useDispatch();
    const history = useHistory();
    const [switcherOpen, setSwitcherOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [notifOpen, setNotifOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    const closeHelp = useCallback(() => setHelpOpen(false), []);
    const unread = useNotifications().filter(x => x.unread).length;
    const [clients, setClients] = useState<Client[]>([]);
    const [search, setSearch] = useState('');
    const rootRef = useRef<HTMLDivElement>(null);
    const selected = clients.find(x => x.id === currentStock?.id);

    const select = (c: Client) => {
        localStorage.setItem(CURRENT_TICKER_KEY, c.ticker);
        dispatch(startSetStock(c.ticker));
    };

    useEffect(() => {
        api().getClientDashboardResult().then(r => {
            const list: Client[] = r.stocks
                .map(s => ({ id: s.id, name: s.companyName || s.combinedTicker, ticker: s.combinedTicker, registry: s.registry }))
                .sort((a, b) => a.name.localeCompare(b.name));
            setClients(list);
            const stored = localStorage.getItem(CURRENT_TICKER_KEY);
            const initial = list.find(x => x.ticker === stored) ?? list.find(x => x.id === user.defaultStockId) ?? list[0];
            if (initial) select(initial);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const q = search.trim().toLowerCase();
    const visible = q ? clients.filter(x => x.name.toLowerCase().includes(q) || x.ticker.toLowerCase().includes(q)) : clients;

    useEffect(() => {
        const onDocClick = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
                setSwitcherOpen(false);
                setMenuOpen(false);
                setNotifOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    const logout = async () => {
        await api().logout();
        window.location.href = '/';
    };

    return <header className="v2-header" ref={rootRef}>
        {/* spec "scrim": dims the page (not the nav) while a header dropdown is open; it sits inside rootRef, so it closes menus itself */}
        {(switcherOpen || menuOpen || notifOpen) && <div className="nav-scrim" onClick={() => { setSwitcherOpen(false); setMenuOpen(false); setNotifOpen(false); }} />}
        <Link to="/" className="logo"><img src="/images/login/logo-brand.svg" alt="Investi" /></Link>

        <nav className="primary-nav">
            <NavLink to="/dashboard/overview" activeClassName="active"><FiHome /> Dashboard</NavLink>
            <NavLink to="/share-registry" activeClassName="active"><FiUsers /> Share Registry</NavLink>
            <NavLink to="/communications" activeClassName="active"><IoMdMegaphone /> Communications</NavLink>
            {/* not a link until the Website Hub screen exists */}
            <span className="inert"><FiGlobe /> Website Hub</span>
        </nav>

        <div className="right">
            <div className="client-switcher">
                <button type="button" className="switcher-button" onClick={() => { setSwitcherOpen(!switcherOpen); setMenuOpen(false); setNotifOpen(false); }}>
                    <span className="client-mark">{selected ? initials(selected.name) : ''}</span>
                    <span className="client-name">{selected?.name ?? 'No clients'}</span>
                    <FiChevronDown className="caret" />
                </button>
                {switcherOpen && <div className="switcher-menu">
                    <div className="menu-title">Switch client</div>
                    <div className="search"><FiSearch /><input placeholder="Search by name or ticker..." value={search} onChange={e => setSearch(e.target.value)} autoFocus /></div>
                    <ul>
                        {visible.map(x => <li key={x.id} className={x.id === selected?.id ? 'selected' : ''}
                            onClick={() => { select(x); setSwitcherOpen(false); setSearch(''); history.push('/dashboard/overview'); }}>
                            <span className="client-mark">{initials(x.name)}</span>
                            <span className="text">
                                <span className="name">{x.name}</span>
                                <span className="meta">{x.ticker}{x.registry && ` · ${x.registry}`}</span>
                            </span>
                            {x.id === selected?.id && <>
                                <span className="tick"><FiCheck /></span>
                                <span className="settings-link"><FiSliders /> Settings</span>
                            </>}
                        </li>)}
                    </ul>
                </div>}
            </div>

            <button type="button" className="icon-button" title="Help & guides" onClick={() => { setHelpOpen(true); setSwitcherOpen(false); setMenuOpen(false); setNotifOpen(false); }}><FiHelpCircle /></button>
            {helpOpen && <HelpPanel onClose={closeHelp} />}
            <div className="notif-bell">
                <button type="button" className="icon-button" title="Notifications" onClick={() => { setNotifOpen(!notifOpen); setSwitcherOpen(false); setMenuOpen(false); }}>
                    <FiBell />
                    {unread > 0 && <span className="unread-badge">{unread}</span>}
                </button>
                {notifOpen && <NotificationsMenu onClose={() => setNotifOpen(false)} />}
            </div>

            <div className="avatar-menu">
                <button type="button" className="avatar-button" onClick={() => { setMenuOpen(!menuOpen); setSwitcherOpen(false); setNotifOpen(false); }}><Avatar user={user} /></button>
                {menuOpen && <div className="user-menu">
                    <div className="who">
                        <Avatar user={user} />
                        <div className="text">
                            <strong>{displayName(user)}</strong>
                            <span className="email">{user.username}</span>
                            {clients.length > 0 && <span className="manages">Manages {clients.length} {clients.length === 1 ? 'client' : 'clients'}</span>}
                        </div>
                    </div>
                    <div className="group">
                        <button type="button" onClick={() => { setMenuOpen(false); history.push('/account'); }}><FiUser /> My Profile</button>
                        <button type="button" onClick={() => { setMenuOpen(false); history.push('/account/security'); }}><ShieldCheckIcon /> Account &amp; Security <span className="meta">2FA on</span></button>
                        <button type="button" onClick={() => { setMenuOpen(false); history.push('/account/notifications'); }}><FiBell /> Notification Preferences</button>
                    </div>
                    {/* spec: only for people who manage more than one company */}
                    {clients.length > 1 && <div className="group">
                        <button type="button" onClick={() => { setMenuOpen(false); history.push('/account/clients'); }}><FiUsers /> My Clients <span className="meta">{clients.length}</span></button>
                    </div>}
                    <div className="group">
                        <button type="button" className="sign-out" onClick={logout}><FiLogOut /> Sign Out</button>
                    </div>
                </div>}
            </div>
        </div>
    </header>;
};

export default Header;
